import { useField } from 'formik';
import React from 'react';

import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
  RadioProps,
} from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import { useHelperTextClasses } from '../BaseTextField';

export type RadioControlValue =
  | string
  | {
    label: React.ReactNode;
    value: string;
    disabled?: boolean;
    contentBelowLabel?: React.ReactNode;
  };

export type RadioControlProps = RadioGroupProps & {
  name: string;
  label?: React.ReactNode;
  values: RadioControlValue[];
  helperText?: string;
  error?: boolean;
  color?: 'primary' | 'secondary';
  inline?: true;
  inlineOptions?: boolean;
  hideHelper?: true;
  disabled?: boolean;
  testId?: string;
};

const getKey = (value: RadioControlValue, index: number) => {
  if (typeof value === 'string') {
    return `${value.replace(' ', '')}-radio-${index}`;
  }

  if (value.value === null) {
    return `radio-${index}`;
  }

  return `${value.value.replace(' ', '')}-radio-${index}`;
};

export const RadioItem: React.VFC<{
  value: RadioControlValue;
  color?: RadioProps['color'];
  className?: string;
  disabled?: boolean;
}> = ({ value, color, className, disabled }) => {
  if (typeof value === 'string') {
    return (
      <FormControlLabel
        label={value}
        value={value}
        disabled={disabled}
        data-testid={`${value}-radio-option`}
        control={
          <Radio color={color} className={className} />
        }
      />
    );
  }

  return (
    <>
      <FormControlLabel
        label={value.label}
        value={value.value}
        disabled={value.disabled}
        data-testid={`${value.value}-radio-option`}
        control={
          <Radio color={color} className={className} />
        }
      />
      {value.contentBelowLabel && (
        <Box pl="33px">
          {value.contentBelowLabel}
        </Box>
      )}
    </>
  );
};


export const useRadioControlStyles = makeStyles()((theme) => ({
  radioRoot: {
    '& svg:first-of-type': {
      transform: 'scale(1)',
    },
  },
  inline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  inlineOptions: {
    flexDirection: 'row',
  },
  error: {
    color: theme.palette.error.main
  }
}));


export const RadioControl: React.FC<RadioControlProps> = ({ testId = 'radio-control', ...props }) => {
  const [ field, meta, helpers ] = useField(props.name);
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const { classes, cx } = useRadioControlStyles();
  const hasError =
    Boolean(props.error && props.helperText) ||
    Boolean(meta.error && meta.touched);
  const helperText = !props.hideHelper && (props.helperText || (meta.touched && meta.error));

  const componentProp = props.inline ? { } : { component: 'legend' };

  return (
    <FormControl
      component="fieldset"
      className={cx([ props.className, {
        [classes.inline]: !!props.inline
      } ])}
      error={hasError}
      disabled={props.disabled}
      color={props.color}
      onBlur={field.onBlur}
      focused={false}
      data-testid={testId}
    >
      {props?.label && <FormLabel {...componentProp}>{props.label}</FormLabel>}
      <RadioGroup
        // aria-label={props.label}
        name={props.name}
        // `value` should not be undefined
        value={field.value ?? null}
        onChange={(_e, value) => helpers.setValue(value || null)}
        row={props.row}
        color={props.color}
        className={cx({ [classes.inlineOptions]: props.inlineOptions })}
      >
        {props.values.map((val, index) => (
          <RadioItem
            key={getKey(val, index)}
            value={val}
            color={props.color}
            className={cx([ classes.radioRoot, {
              [classes.error]: hasError
            } ])}
          />
        ))}
      </RadioGroup>
      {helperText && (
        <FormHelperText className={helperTextClasses.root}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
